import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogContent, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { prepareDeliveryDeletion, deleteSavedDelivery } from '@/utils/deliveryOutbox';

const LEGACY_DELETION = {
  prepare: prepareDeliveryDeletion, remove: deleteSavedDelivery,
  button: 'Delete saved copy', title: 'Delete this saved copy from this device?',
  description: "Any confirmation details or media in this copy that haven't reached the server will be permanently lost. This will not delete anything already saved on the server.",
  success: 'Saved copy deleted from this device.',
};

export default function DeleteSavedDeliveryButton({ orderId, label, owns, onDeleted, deletion = LEGACY_DELETION }) {
  const [selection, setSelection] = useState(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const alive = useRef(false);
  const busy = useRef(false);
  const generation = useRef(0);
  const trigger = useRef(null);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; generation.current += 1; };
  }, []);
  const current = (attempt) => alive.current && owns() && generation.current === attempt;
  const prepare = async () => {
    if (busy.current || !owns()) return;
    busy.current = true; setPending(true); setError('');
    const attempt = ++generation.current;
    try {
      const snapshot = await deletion.prepare(orderId, () => current(attempt));
      if (!current(attempt)) return;
      if (!snapshot) { setError('This saved copy is already absent. Refresh saved deliveries.'); return; }
      // Freeze only the displayed identity and local revision, never media/proof.
      setSelection({ snapshot, label });
    } catch (failure) {
      if (current(attempt)) setError(failure.message || 'Could not prepare deletion. Nothing was deleted.');
    } finally {
      busy.current = false;
      if (current(attempt)) setPending(false);
    }
  };
  const confirm = async (event) => {
    event.preventDefault(); // Keep the confirmation mounted until commit/failure.
    if (!selection || busy.current || !owns()) return;
    busy.current = true; setPending(true); setError('');
    const attempt = generation.current;
    const { snapshot } = selection;
    let committed = false;
    try {
      const result = await deletion.remove(snapshot, () => current(attempt));
      committed = true;
      if (current(attempt)) {
        setSelection(null);
        toast.success(result === 'deleted' ? deletion.success : 'This saved copy was already absent.');
      }
      // The commit is real even if the originating row unmounted. The parent
      // reconciles storage without inventing a submission or deleting again.
      await onDeleted?.(snapshot);
    } catch (failure) {
      if (current(attempt)) {
        setSelection(null); // New selection/consent required after any failure.
        setError(committed ? 'The saved copy is gone, but the list could not refresh. Reopen saved deliveries; do not delete again.'
          : failure.message || 'Could not delete this saved copy. Refresh and review it again.');
      }
    } finally {
      busy.current = false;
      if (current(attempt)) setPending(false);
    }
  };
  return <>
    <button ref={trigger} type="button" disabled={pending}
      className="mt-2 rounded-lg border border-white px-3 py-2 disabled:opacity-50"
      onClick={prepare}>{deletion.button}</button>
    {error && <p role="alert" className="mt-2">{error}</p>}
    <AlertDialog open={!!selection} onOpenChange={(open) => {
      if (!open && !busy.current) { generation.current += 1; setSelection(null); }
    }}>
      <AlertDialogContent onCloseAutoFocus={(event) => { event.preventDefault(); trigger.current?.focus(); }}>
        <AlertDialogTitle>{deletion.title}</AlertDialogTitle>
        <AlertDialogDescription>
          <span className="block mb-2">{selection?.label}</span>
          {deletion.description}
        </AlertDialogDescription>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>Keep saved copy</AlertDialogCancel>
          <AlertDialogAction disabled={pending} onClick={confirm} className="bg-red-700 hover:bg-red-800">
            {pending ? 'Deleting…' : deletion.button}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </>;
}
